import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ArrowRight, CreditCard, Building, Banknote, Receipt, CheckCircle } from "lucide-react";

export default function StallApplication() {
  const { ptaId } = useParams();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [ptaInfo, setPtaInfo] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [step, setStep] = useState(1);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [submittedApplication, setSubmittedApplication] = useState<any>(null);
  const [formData, setFormData] = useState({
    businessName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    description: "",
    eventId: "",
    keepForFuture: false,
  });

  useEffect(() => {
    if (ptaId) {
      fetchPTAInfo();
    }
  }, [ptaId]);

  useEffect(() => {
    if (ptaInfo?.id) {
      fetchEvents();
      fetchSettings();
    }
  }, [ptaInfo?.id]);

  const fetchPTAInfo = async () => {
    try {
      // Use the database function which is SECURITY DEFINER
      const { data, error } = await supabase
        .rpc('get_pta_by_short_code', { short_code: ptaId });

      if (!error && data && data.length > 0) {
        setPtaInfo(data[0]);
      } else {
        console.error("PTA not found:", error);
        setPageLoading(false);
      }
    } catch (err) {
      console.error("Error fetching PTA:", err);
      setPageLoading(false);
    }
  };

  const fetchEvents = async () => {
    const { data } = await supabase
      .from("events")
      .select("*")
      .eq("pta_id", ptaInfo?.id)
      .eq("status", "active")
      .gte("event_date", new Date().toISOString())
      .order("event_date", { ascending: true });

    if (data) {
      setEvents(data);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("stall_application_settings")
        .select("*")
        .eq("pta_id", ptaInfo?.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching settings:", error);
        // Default to accepting applications if there's an error
        setSettings({ accept_applications: true });
      } else if (data) {
        setSettings(data);
      } else {
        // No settings exist, default to accepting applications
        setSettings({ accept_applications: true });
      }
    } catch (err) {
      console.error("Exception fetching settings:", err);
      setSettings({ accept_applications: true });
    } finally {
      setPageLoading(false);
    }
  };

  const validateStep1 = () => {
    if (!formData.eventId) {
      toast.error("Please select an event");
      return false;
    }
    if (!formData.businessName.trim()) {
      toast.error("Please enter your business name");
      return false;
    }
    if (!formData.contactName.trim()) {
      toast.error("Please enter your contact name");
      return false;
    }
    if (!formData.contactEmail.trim()) {
      toast.error("Please enter your email");
      return false;
    }
    if (!formData.contactPhone.trim()) {
      toast.error("Please enter your phone number");
      return false;
    }
    if (!formData.description.trim()) {
      toast.error("Please describe what you will be selling");
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      // If there are prohibited items, go to agreement step
      if (settings?.prohibited_items) {
        setStep(2);
      } else {
        // No prohibited items, go straight to payment step
        setStep(3);
      }
    } else if (step === 2) {
      if (!agreementAccepted) {
        toast.error("Please accept the terms to continue");
        return;
      }
      // Go to payment step
      setStep(3);
    }
  };

  const getTotalSteps = () => {
    return settings?.prohibited_items ? 3 : 2;
  };

  const handleSubmitWithPayment = async (paymentMethod: string) => {
    if (!paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }

    setLoading(true);
    setSelectedPaymentMethod(paymentMethod);

    try {
      const selectedEvent = events.find(e => e.id === formData.eventId);
      const autoDeleteDate = !formData.keepForFuture && selectedEvent
        ? new Date(new Date(selectedEvent.event_date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : null;

      const { data: application, error } = await supabase
        .from("stall_applications")
        .insert({
          pta_id: ptaInfo?.id,
          event_id: formData.eventId,
          business_name: formData.businessName,
          contact_name: formData.contactName,
          contact_email: formData.contactEmail,
          contact_phone: formData.contactPhone,
          description: formData.description,
          payment_amount: settings?.stall_fee || 25.00,
          keep_for_future: formData.keepForFuture,
          auto_delete_date: autoDeleteDate,
          payment_method: paymentMethod,
          status: "approved", // Auto-approve all applications
        })
        .select()
        .single();

      // If the business opted in to keep details, save to contacts immediately
      if (!error && application && formData.keepForFuture) {
        try {
          const { data: existingContact } = await supabase
            .from("stall_contacts")
            .select("id")
            .eq("pta_id", ptaInfo?.id)
            .eq("contact_email", formData.contactEmail)
            .maybeSingle();

          if (existingContact) {
            await supabase
              .from("stall_contacts")
              .update({
                business_name: formData.businessName,
                contact_name: formData.contactName,
                contact_phone: formData.contactPhone,
                description: formData.description,
                last_stall_date: selectedEvent?.event_date?.split('T')[0] || new Date().toISOString().split('T')[0],
              })
              .eq("id", existingContact.id);
          } else {
            await supabase
              .from("stall_contacts")
              .insert({
                pta_id: ptaInfo?.id,
                business_name: formData.businessName,
                contact_name: formData.contactName,
                contact_email: formData.contactEmail,
                contact_phone: formData.contactPhone,
                description: formData.description,
                last_stall_date: selectedEvent?.event_date?.split('T')[0] || new Date().toISOString().split('T')[0],
              });
          }
        } catch (contactError) {
          console.error("Failed to save contact:", contactError);
        }
      }

      if (error) throw error;

      setSubmittedApplication(application);

      // If Stripe payment, redirect to checkout
      if (paymentMethod === 'stripe') {
        const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
          'create-stall-payment-checkout',
          { body: { applicationId: application.id } }
        );

        if (checkoutError) {
          console.error("Checkout error:", checkoutError);
          toast.error("Failed to create payment session. Please try again or choose another payment method.");
          setLoading(false);
          return;
        }

        if (checkoutData?.url) {
          window.location.href = checkoutData.url;
          return;
        }
      }

      // For non-Stripe payments, show confirmation
      toast.success("Application submitted successfully!");
      setStep(4);
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!settings?.accept_applications) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            {ptaInfo?.logo_url && (
              <div className="flex justify-center mb-4">
                <img src={ptaInfo.logo_url} alt={ptaInfo.name} className="h-20 object-contain" />
              </div>
            )}
            <CardTitle>Applications Closed</CardTitle>
            <CardDescription>
              {ptaInfo?.name || "This PTA"} is not currently accepting stall applications.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 py-12">
      <Card className="max-w-2xl mx-auto">
        <CardHeader className="text-center">
          {ptaInfo?.logo_url && (
            <div className="flex justify-center mb-4">
              <img src={ptaInfo.logo_url} alt={ptaInfo.name} className="h-20 object-contain" />
            </div>
          )}
          <CardTitle>Stall Space Application</CardTitle>
          <CardDescription>
            Apply for a stall space at {ptaInfo?.name || "our"} upcoming events
          </CardDescription>
          <div className="flex justify-center gap-2 mt-4">
            <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
            {settings?.prohibited_items && (
              <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
            )}
            <div className={`w-3 h-3 rounded-full ${step >= getTotalSteps() ? 'bg-primary' : 'bg-muted'}`} />
          </div>
        </CardHeader>
        <CardContent>
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="eventId">Select Event *</Label>
                <Select
                  value={formData.eventId}
                  onValueChange={(value) => setFormData({ ...formData, eventId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.name} - {new Date(event.event_date).toLocaleDateString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessName">Business Name *</Label>
                <Input
                  id="businessName"
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name *</Label>
                <Input
                  id="contactName"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Email *</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Phone *</Label>
                <Input
                  id="contactPhone"
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">What will you be selling? *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="p-4 border border-border rounded-lg space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="keepForFuture" className="font-semibold">Keep my details for future events</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow us to store your business contact information
                    </p>
                  </div>
                  <Switch
                    id="keepForFuture"
                    checked={formData.keepForFuture}
                    onCheckedChange={(checked) => setFormData({ ...formData, keepForFuture: checked })}
                  />
                </div>
                <div className="text-xs text-muted-foreground border-t pt-3">
                  <p className="font-medium mb-1">Data Protection Notice (GDPR)</p>
                  <p>
                    By toggling this option, you consent to us storing your business name, contact name, 
                    email, phone number, and description for the purpose of contacting you about future 
                    stall opportunities. Your data will only be used for this purpose and will not be 
                    shared with third parties. You can request deletion of your data at any time by 
                    contacting us. If you do not toggle this option, your data will be automatically 
                    deleted after the event.
                  </p>
                </div>
              </div>

              <Button onClick={handleNextStep} className="w-full" disabled={loading}>
                {settings?.prohibited_items ? (
                  <>
                    Continue <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  <>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Submit Application
                  </>
                )}
              </Button>
            </div>
          )}

          {step === 2 && settings?.prohibited_items && (
            <div className="space-y-6">
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <h4 className="font-semibold mb-3 text-destructive">Prohibited Items Agreement</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {settings.prohibited_items}
                </p>
              </div>

              <div className="flex items-start space-x-3 p-4 border border-border rounded-lg bg-muted/30">
                <Checkbox
                  id="agreement"
                  checked={agreementAccepted}
                  onCheckedChange={(checked) => setAgreementAccepted(checked === true)}
                />
                <Label htmlFor="agreement" className="text-sm leading-relaxed cursor-pointer">
                  I acknowledge that I have read and understood the prohibited items list. I agree not to sell any of the listed items and accept that my application may be declined or my stall removed if I fail to comply with these terms.
                </Label>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={handleNextStep}
                  className="flex-1"
                  disabled={!agreementAccepted}
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              {/* Payment Amount */}
              <div className="text-center py-4 border-y border-border">
                <p className="text-sm text-muted-foreground mb-1">Stall Fee</p>
                <p className="text-4xl font-bold text-primary">
                  £{(settings?.stall_fee || 25).toFixed(2)}
                </p>
              </div>

              {/* Payment Options */}
              <div className="space-y-4">
                <h4 className="font-semibold">Select Payment Method</h4>
                
                {/* Stripe Payment */}
                {settings?.stripe_enabled && (
                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod('stripe')}
                    className={`w-full text-left border rounded-lg p-4 space-y-2 transition-colors ${
                      selectedPaymentMethod === 'stripe' 
                        ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <CreditCard className="h-5 w-5 text-primary" />
                      Pay by Card
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Pay securely online with your credit or debit card.
                    </p>
                  </button>
                )}

                {/* BACS Payment */}
                {settings?.bacs_enabled && settings.bacs_account_number && (
                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod('bacs')}
                    className={`w-full text-left border rounded-lg p-4 space-y-2 transition-colors ${
                      selectedPaymentMethod === 'bacs' 
                        ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <Building className="h-5 w-5 text-primary" />
                      Bank Transfer (BACS)
                    </div>
                    <div className="text-sm space-y-1 bg-muted/50 rounded p-3 mt-2">
                      <p><span className="text-muted-foreground">Sort Code:</span> {settings.bacs_sort_code}</p>
                      <p><span className="text-muted-foreground">Account Number:</span> {settings.bacs_account_number}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Please use your business name as the payment reference.
                      </p>
                    </div>
                  </button>
                )}

                {/* Cash Payment */}
                {settings?.cash_enabled && (
                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod('cash')}
                    className={`w-full text-left border rounded-lg p-4 space-y-2 transition-colors ${
                      selectedPaymentMethod === 'cash' 
                        ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <Banknote className="h-5 w-5 text-primary" />
                      Cash Payment
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {settings.cash_instructions || 'Please bring cash payment on the day of the event.'}
                    </p>
                  </button>
                )}

                {/* Cheque Payment */}
                {settings?.cheque_enabled && settings.cheque_payable_to && (
                  <button
                    type="button"
                    onClick={() => setSelectedPaymentMethod('cheque')}
                    className={`w-full text-left border rounded-lg p-4 space-y-2 transition-colors ${
                      selectedPaymentMethod === 'cheque' 
                        ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <Receipt className="h-5 w-5 text-primary" />
                      Cheque Payment
                    </div>
                    <div className="text-sm space-y-1 mt-2">
                      <p><span className="text-muted-foreground">Make payable to:</span> {settings.cheque_payable_to}</p>
                      {settings.cheque_address && (
                        <p><span className="text-muted-foreground">Send to:</span> {settings.cheque_address}</p>
                      )}
                    </div>
                  </button>
                )}

                {!settings?.stripe_enabled && !settings?.bacs_enabled && !settings?.cash_enabled && !settings?.cheque_enabled && (
                  <div className="text-center text-muted-foreground p-4">
                    <p>Payment methods will be confirmed after your application is reviewed.</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(settings?.prohibited_items ? 2 : 1)}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={() => handleSubmitWithPayment(selectedPaymentMethod!)}
                  className="flex-1"
                  disabled={loading || !selectedPaymentMethod}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {selectedPaymentMethod === 'stripe' ? 'Proceed to Payment' : 'Submit Application'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && submittedApplication && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Application Submitted!</h3>
                <p className="text-muted-foreground">
                  Thank you for your application. We will review it and get back to you soon.
                </p>
              </div>

              {selectedPaymentMethod && selectedPaymentMethod !== 'stripe' && (
                <div className="bg-muted/50 rounded-lg p-4 text-left space-y-3">
                  <h4 className="font-semibold">Payment Instructions</h4>
                  
                  {selectedPaymentMethod === 'bacs' && settings?.bacs_account_number && (
                    <div className="text-sm space-y-1">
                      <p className="font-medium">Bank Transfer (BACS)</p>
                      <p><span className="text-muted-foreground">Sort Code:</span> {settings.bacs_sort_code}</p>
                      <p><span className="text-muted-foreground">Account Number:</span> {settings.bacs_account_number}</p>
                      <p><span className="text-muted-foreground">Amount:</span> £{(settings?.stall_fee || 25).toFixed(2)}</p>
                      <p><span className="text-muted-foreground">Reference:</span> {formData.businessName || submittedApplication.business_name}</p>
                    </div>
                  )}

                  {selectedPaymentMethod === 'cash' && (
                    <div className="text-sm">
                      <p className="font-medium">Cash Payment</p>
                      <p className="text-muted-foreground">
                        {settings?.cash_instructions || 'Please bring cash payment on the day of the event.'}
                      </p>
                      <p className="mt-2"><span className="text-muted-foreground">Amount:</span> £{(settings?.stall_fee || 25).toFixed(2)}</p>
                    </div>
                  )}

                  {selectedPaymentMethod === 'cheque' && settings?.cheque_payable_to && (
                    <div className="text-sm space-y-1">
                      <p className="font-medium">Cheque Payment</p>
                      <p><span className="text-muted-foreground">Make payable to:</span> {settings.cheque_payable_to}</p>
                      {settings.cheque_address && (
                        <p><span className="text-muted-foreground">Send to:</span> {settings.cheque_address}</p>
                      )}
                      <p><span className="text-muted-foreground">Amount:</span> £{(settings?.stall_fee || 25).toFixed(2)}</p>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={() => {
                  setStep(1);
                  setSelectedPaymentMethod(null);
                  setSubmittedApplication(null);
                  setAgreementAccepted(false);
                  setFormData({
                    businessName: "",
                    contactName: "",
                    contactEmail: "",
                    contactPhone: "",
                    description: "",
                    eventId: "",
                    keepForFuture: false,
                  });
                }}
                variant="outline"
                className="w-full"
              >
                Submit Another Application
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
